import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { useStore } from "../context/StoreContext";
import { inr } from "../lib/api";

export function ComingSoonBadge({ small = false }) {
  return (
    <span data-testid="coming-soon-badge-item"
      className={`badge-sweep inline-flex items-center bg-gradient-to-r from-[#7E1E3F]/20 via-[#C9A24B]/30 to-[#7E1E3F]/20 border border-[#7E1E3F]/50 text-[#5C1230] font-mono-lux ${small ? "text-[9px] px-2 py-0.5" : "text-[10px] px-2.5 py-1"} tracking-[0.2em] rounded-full uppercase backdrop-blur-sm`}>
      Coming Soon
    </span>
  );
}

export default function ProductCard({ product, onNotify, index = 0 }) {
  const { addToCart, wishlist, toggleWishlist } = useStore();
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const coming = product.availability !== "available";
  const wished = wishlist.includes(product.slug);
  const off = product.mrp > product.price ? Math.round(((product.mrp - product.price) / product.mrp) * 100) : 0;

  const onMove = (e) => {
    if (reduced || !ref.current || e.pointerType === "touch") return;
    const r = ref.current.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;
    setTilt({ x, y });
  };

  return (
    <motion.div
      ref={ref}
      initial={reduced ? false : { opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.7, delay: (index % 4) * 0.08, ease: [0.22, 1, 0.36, 1] }}
      onPointerMove={onMove}
      onPointerLeave={() => setTilt({ x: 0, y: 0 })}
      className="group card-surface rounded-2xl overflow-hidden gold-ring-hover hover:-translate-y-2"
      data-testid={`product-card-${product.slug}`}
    >
      <Link to={`/product/${product.slug}`} className="block relative aspect-[4/5] overflow-hidden bg-[#FBF7F1]" data-testid={`product-link-${product.slug}`}>
        <img
          src={product.images[0]}
          alt={product.name}
          loading="lazy"
          className={`w-full h-full transition-[transform,filter] duration-700 ease-out group-hover:scale-[1.04] group-hover:brightness-105 ${product.images[0].startsWith("/assets/") ? "object-contain bg-white p-3" : "object-cover"}`}
          style={reduced ? {} : { transform: `translate(${tilt.x * 8}px, ${tilt.y * 8}px) scale(1.02)` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#FFFFFF]/70 via-transparent to-transparent" />
        {coming && (
          <div className="absolute top-3 left-3"><ComingSoonBadge /></div>
        )}
        {!coming && off > 0 && (
          <span className="absolute top-3 left-3 bg-[#7E1E3F] text-[#FFFFFF] text-[10px] font-semibold px-2 py-1 rounded-full" data-testid="discount-badge">{off}% OFF</span>
        )}
        <button
          onClick={(e) => { e.preventDefault(); toggleWishlist(product.slug); }}
          className={`absolute top-3 right-3 p-2 rounded-full backdrop-blur-md transition-all duration-300 ${wished ? "bg-[#7E1E3F] text-[#FFFFFF]" : "bg-[#FFFFFF]/50 text-[#2E1A22] opacity-0 group-hover:opacity-100"}`}
          data-testid={`wishlist-toggle-${product.slug}`} aria-label="Wishlist">
          <Heart size={15} fill={wished ? "currentColor" : "none"} />
        </button>
      </Link>
      <div className="p-4">
        <Link to={`/product/${product.slug}`}>
          <h3 className="font-serif-lux text-lg text-[#2E1A22] leading-snug line-clamp-1 group-hover:text-[#5C1230] transition-colors">{product.name}</h3>
        </Link>
        <div className="mt-2 flex items-baseline gap-2">
          <span className="text-[#7E1E3F] font-semibold" data-testid={`price-${product.slug}`}>{inr(product.price)}</span>
          {product.mrp > product.price && <span className="text-xs text-[#857078] line-through">{inr(product.mrp)}</span>}
        </div>
        <div className="mt-3">
          {coming ? (
            <button onClick={() => onNotify?.(product)}
              className="w-full text-xs tracking-[0.15em] uppercase font-medium border border-[#7E1E3F]/40 text-[#5C1230] rounded-full py-2.5 hover:bg-[#7E1E3F]/10 transition-colors"
              data-testid={`notify-me-${product.slug}`}>
              Notify Me
            </button>
          ) : (
            <button onClick={() => addToCart(product)}
              className="w-full text-xs tracking-[0.15em] uppercase font-semibold bg-[#7E1E3F] text-[#FFFFFF] rounded-full py-2.5 hover:bg-[#5C1230] transition-colors active:scale-[0.98]"
              data-testid={`add-to-cart-${product.slug}`}>
              Add to Cart
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
}
